import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  useCancelAccountctlRunMutation,
  useCreateAccountctlRunMutation,
  useFixAccountctlOrgMutation,
  useFlagAccountctlRunMutation,
  useGetAccountctlRunQuery,
} from '@/services/accountctlApi';
import { PalmettoButton, PalmettoTag, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AccountctlRunStatus, RunEvent } from 'types';

import { useRunStream } from './hooks/useRunStream';

const formatRunDate = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} ${time}`;
};

type StepStatus = 'pending' | 'running' | 'ok' | 'warn' | 'error';

interface TimelineStep {
  index: number;
  action: string;
  label?: string;
  status: StepStatus;
  message?: string;
  detail?: string;
  durationMs?: number;
  logs: Array<{ level: 'info' | 'warn' | 'error'; message: string; ts: string }>;
  startedAt?: string;
}

const stepStatusColor = {
  pending: 'grey',
  running: 'volt',
  ok: 'success',
  warn: 'warning',
  error: 'error',
} as const satisfies Record<StepStatus, 'grey' | 'volt' | 'success' | 'warning' | 'error'>;

const runStatusColor = {
  running: 'volt',
  ok: 'success',
  error: 'error',
  cancelled: 'warning',
} as const satisfies Record<AccountctlRunStatus, 'volt' | 'success' | 'error' | 'warning'>;

/**
 * Fold the linear `RunEvent[]` into a step-indexed timeline. Each `step.*`
 * event carries an `index` we can key off; `log` events attach to the most
 * recent step (or to a synthetic pre-step bucket if they arrive before any
 * step started). Terminal step events overwrite the in-progress step's
 * status and capture the optional `message`/`detail`/`durationMs`.
 */
const foldEvents = (
  events: RunEvent[],
): {
  steps: TimelineStep[];
  preStepLogs: Array<{ level: 'info' | 'warn' | 'error'; message: string; ts: string }>;
  runStarted?: Extract<RunEvent, { type: 'run.started' }>;
  runFinished?: Extract<RunEvent, { type: 'run.finished' }>;
  preconditionFailed?: Extract<RunEvent, { type: 'precondition.failed' }>;
  hasFallbackWarning: boolean;
} => {
  const stepsByIndex = new Map<number, TimelineStep>();
  const preStepLogs: Array<{ level: 'info' | 'warn' | 'error'; message: string; ts: string }> = [];
  let runStarted: Extract<RunEvent, { type: 'run.started' }> | undefined;
  let runFinished: Extract<RunEvent, { type: 'run.finished' }> | undefined;
  let preconditionFailed: Extract<RunEvent, { type: 'precondition.failed' }> | undefined;
  let lastStepIndex: number | undefined;
  let hasFallbackWarning = false;

  const ensureStep = (index: number, action: string, label?: string): TimelineStep => {
    const existing = stepsByIndex.get(index);
    if (existing) {
      if (label && !existing.label) existing.label = label;
      return existing;
    }
    const fresh: TimelineStep = { index, action, label, status: 'pending', logs: [] };
    stepsByIndex.set(index, fresh);
    return fresh;
  };

  for (const event of events) {
    switch (event.type) {
      case 'run.started':
        runStarted = event;
        break;
      case 'run.finished':
        runFinished = event;
        break;
      case 'precondition.failed':
        preconditionFailed = event;
        break;
      case 'step.started': {
        const step = ensureStep(event.index, event.action, event.label);
        step.status = 'running';
        step.startedAt = event.ts;
        lastStepIndex = event.index;
        break;
      }
      case 'step.ok': {
        const step = ensureStep(event.index, event.action, event.label);
        step.status = 'ok';
        step.detail = event.detail;
        step.durationMs = event.durationMs;
        lastStepIndex = event.index;
        break;
      }
      case 'step.warn': {
        const step = ensureStep(event.index, event.action, event.label);
        step.status = 'warn';
        step.message = event.message;
        step.detail = event.detail;
        lastStepIndex = event.index;
        break;
      }
      case 'step.error': {
        const step = ensureStep(event.index, event.action, event.label);
        step.status = 'error';
        step.message = event.message;
        lastStepIndex = event.index;
        break;
      }
      case 'log': {
        const entry = { level: event.level, message: event.message, ts: event.ts };
        if (lastStepIndex === undefined) {
          preStepLogs.push(entry);
        } else {
          const step = stepsByIndex.get(lastStepIndex);
          if (step) step.logs.push(entry);
        }
        if (event.reason === 'fallback') {
          hasFallbackWarning = true;
        }
        break;
      }
      default:
        break;
    }
  }

  const steps = [...stepsByIndex.values()].toSorted((a, b) => a.index - b.index);
  return { steps, preStepLogs, runStarted, runFinished, preconditionFailed, hasFallbackWarning };
};

interface PreconditionFixBannerProps {
  scenarioId: string;
  fixable: Array<{ category: string; message: string }>;
  manual: Array<{ category: string; message: string }>;
}

const PreconditionFixBanner = ({ scenarioId, fixable, manual }: PreconditionFixBannerProps) => {
  const navigate = useNavigate();
  const [fixOrg, { isLoading: isFixing }] = useFixAccountctlOrgMutation();
  const [createRun, { isLoading: isRerunning }] = useCreateAccountctlRunMutation();
  const [fixResult, setFixResult] = useState<{
    allClear: boolean;
    remaining: Array<{ category: string; message: string; fixable: boolean }>;
  } | null>(null);
  const [fixError, setFixError] = useState<string | null>(null);

  const handleFix = useCallback(async () => {
    setFixError(null);
    try {
      const result = await fixOrg({ scenarioId }).unwrap();
      setFixResult(result);
    } catch (err) {
      setFixError(err instanceof Error ? err.message : 'Fix failed');
    }
  }, [fixOrg, scenarioId]);

  const handleRerun = useCallback(async () => {
    try {
      const result = await createRun({ scenarioId }).unwrap();
      navigate(`/internal/accountctl/runs/${result.runId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start run');
    }
  }, [createRun, navigate, scenarioId]);

  if (fixResult) {
    const hasRemaining = fixResult.remaining.length > 0;
    return (
      <div
        className={`rounded-6 border p-16 ${
          hasRemaining
            ? 'border-notification-border-warning bg-notification-canvas-warning'
            : 'border-notification-border-success bg-notification-canvas-success'
        }`}
      >
        <div className="flex items-start justify-between gap-16">
          <div className="flex flex-col gap-4">
            <div className="font-bold">
              {fixResult.allClear ? 'Org fixed — all preconditions met' : 'Fixed what we could'}
            </div>
            {hasRemaining && (
              <ul className="flex flex-col gap-2 text-sm">
                {fixResult.remaining.map((f) => (
                  <li key={`${f.category}-${f.message}`} className="font-mono">
                    [{f.category}] {f.message}
                    {!f.fixable && <span className="ml-4 text-text-secondary">[manual]</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <PalmettoButton type="button" emphasis="primary" size="small" onClick={handleRerun} disabled={isRerunning}>
            {isRerunning ? 'Starting…' : 'Re-run scenario'}
          </PalmettoButton>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-6 border border-notification-border-error bg-notification-canvas-error p-16">
      <div className="flex items-start justify-between gap-16">
        <div className="flex flex-col gap-8">
          <div className="font-bold">Preconditions not met</div>
          {fixable.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold">Auto-fixable:</div>
              <ul className="gap-1 flex flex-col font-mono text-sm">
                {fixable.map((f) => (
                  <li key={`${f.category}-${f.message}`}>
                    [{f.category}] {f.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {manual.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold">Manual steps required:</div>
              <ul className="gap-1 flex flex-col font-mono text-sm">
                {manual.map((f) => (
                  <li key={`${f.category}-${f.message}`}>
                    [{f.category}] {f.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {fixError && <div className="text-sm text-text-error">{fixError}</div>}
        </div>
        {fixable.length > 0 && (
          <PalmettoButton type="button" emphasis="primary" size="small" onClick={handleFix} disabled={isFixing}>
            {isFixing ? 'Fixing…' : 'Fix org'}
          </PalmettoButton>
        )}
      </div>
    </div>
  );
};

interface LogLineProps {
  level: 'info' | 'warn' | 'error';
  message: string;
}

/**
 * A runner crash surfaces as a `log` at `level: 'error'`, not a `step.error`
 * (it happens before any step starts). Reuse the step.error notification
 * styling so it reads with the same urgency instead of blending in with the
 * plain `warn` fallback-auth notices around it.
 */
const LogLine = ({ level, message }: LogLineProps) => {
  if (level === 'error') {
    return (
      <div className="rounded-6 bg-notification-canvas-error p-8 text-sm text-text-error">
        <span className="font-mono">[{level}]</span> {message}
      </div>
    );
  }
  return (
    <div>
      <span className="font-mono">[{level}]</span> {message}
    </div>
  );
};

interface TimelineRowProps {
  step: TimelineStep;
}

const TimelineRow = ({ step }: TimelineRowProps) => {
  const title = step.label ?? step.action;
  return (
    <div className="flex flex-col gap-4 rounded-6 border border-border-default p-16">
      <div className="flex items-center gap-8">
        <PalmettoTag color={stepStatusColor[step.status]} label={step.status} size="sm" />
        <span className="font-bold">
          {step.index + 1}. {title}
        </span>
        {step.durationMs !== undefined && <span className="text-sm text-text-secondary">{step.durationMs}ms</span>}
      </div>
      {step.detail && step.status === 'ok' && (
        <div className="text-sm text-text-secondary">
          {step.action === 'create_account' ? (
            <a href={`/accounts/${step.detail}`} target="_blank" rel="noreferrer" className="text-text-link">
              {step.detail} ↗
            </a>
          ) : (
            step.detail
          )}
        </div>
      )}
      {step.status === 'warn' && step.message && (
        <div className="rounded-6 bg-notification-canvas-warning p-8 text-sm text-text-warning">
          {step.message}
          {step.detail && step.detail !== step.message && (
            <div className="mt-4 text-text-secondary">{step.detail}</div>
          )}
        </div>
      )}
      {step.status === 'error' && step.message && (
        <div className="rounded-6 bg-notification-canvas-error p-8 text-sm text-text-error">{step.message}</div>
      )}
      {step.logs.length > 0 && (
        <div className="flex flex-col gap-2 text-sm text-text-secondary">
          {step.logs.map((log, i) => (
            <LogLine key={`${step.index}-${i}`} level={log.level} message={log.message} />
          ))}
        </div>
      )}
    </div>
  );
};

export const RunPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: run, refetch } = useGetAccountctlRunQuery(id ?? '', { skip: !id });
  const { events, isFinished } = useRunStream(id);
  const [cancelRun, { isLoading: isCancelling }] = useCancelAccountctlRunMutation();

  // Prefer the SSE-fed events (live) but fall back to the persisted doc until
  // the stream connects. After `run.finished`, refetch the doc so the page
  // reflects the canonical terminal state without waiting for a manual reload.
  const eventList = useMemo<RunEvent[]>(() => {
    if (events.length > 0) return events;
    return run?.events ?? [];
  }, [events, run?.events]);

  useEffect(() => {
    if (isFinished && id) {
      refetch();
    }
  }, [id, isFinished, refetch]);

  const folded = useMemo(() => foldEvents(eventList), [eventList]);
  const accountId = folded.runFinished?.accountId ?? run?.accountId;
  const status: AccountctlRunStatus = folded.runFinished ? folded.runFinished.status : (run?.status ?? 'running');
  const runError = folded.runFinished?.error ?? run?.error;

  const [flagNote, setFlagNote] = useState('');
  const [flagging, setFlagging] = useState(false);
  const [flagRun, { isLoading: isFlagging }] = useFlagAccountctlRunMutation();

  const handleCancel = useCallback(async () => {
    if (!id) return;
    try {
      const result = await cancelRun(id).unwrap();
      if (result.cancelled) {
        toast.success('Cancel requested; the runner will stop at the next step boundary.');
      } else {
        toast.error('Could not cancel this run — it may have already finished.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel run';
      toast.error(message);
    }
  }, [cancelRun, id]);

  const handleFlag = useCallback(async () => {
    if (!id) return;
    try {
      await flagRun({ id, note: flagNote.trim() || undefined }).unwrap();
      setFlagging(false);
      setFlagNote('');
      toast.success('Flagged for engineer review.');
    } catch (err) {
      const apiMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { message?: string } }).data?.message
          : undefined;
      toast.error(apiMessage ?? (err instanceof Error ? err.message : 'Failed to flag run'));
    }
  }, [flagRun, flagNote, id]);

  const handleOpenFlagging = useCallback(() => setFlagging(true), []);
  const handleCancelFlagging = useCallback(() => {
    setFlagging(false);
    setFlagNote('');
  }, []);
  const handleFlagNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFlagNote(e.target.value),
    [],
  );
  const handleFlagNoteKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') void handleFlag();
      if (e.key === 'Escape') {
        setFlagging(false);
        setFlagNote('');
      }
    },
    [handleFlag],
  );

  const alreadyFlagged = run?.flagged ?? false;
  const showCancelButton = status === 'running' && !isFinished;
  const showFlagButton = !showCancelButton && !alreadyFlagged;

  return (
    <DsmPageContainer title={`Test Account Generator — run ${id ?? ''}`}>
      <PageHeader title={run?.scenarioId ?? 'Run'}>
        <div className="flex items-center gap-8">
          <PalmettoTag color={runStatusColor[status]} label={status} size="sm" />
          {showCancelButton && (
            <PalmettoButton
              type="button"
              emphasis="secondary"
              size="small"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling…' : 'Cancel run'}
            </PalmettoButton>
          )}
          {showFlagButton && !flagging && (
            <PalmettoButton
              type="button"
              emphasis="secondary"
              size="small"
              onClick={handleOpenFlagging}
              style={{ whiteSpace: 'nowrap' }}
            >
              Flag for review
            </PalmettoButton>
          )}
          {showFlagButton && flagging && (
            <div className="flex items-center gap-8">
              <PalmettoTextInput
                id="flag-note"
                label="Note"
                value={flagNote}
                onChange={handleFlagNoteChange}
                placeholder="Optional note…"
                style={{ width: 200 }}
                autoFocus
                onKeyDown={handleFlagNoteKeyDown}
              />
              <PalmettoButton
                type="button"
                emphasis="primary"
                size="small"
                onClick={handleFlag}
                disabled={isFlagging}
                style={{ whiteSpace: 'nowrap' }}
              >
                {isFlagging ? 'Flagging…' : 'Submit'}
              </PalmettoButton>
              <PalmettoButton
                type="button"
                emphasis="secondary"
                size="small"
                onClick={handleCancelFlagging}
                style={{ whiteSpace: 'nowrap' }}
              >
                Cancel
              </PalmettoButton>
            </div>
          )}
          <Link to="/internal/accountctl">
            <PalmettoButton type="button" emphasis="secondary" size="small" style={{ whiteSpace: 'nowrap' }}>
              Back to scenarios
            </PalmettoButton>
          </Link>
        </div>
      </PageHeader>

      <div className="mx-6 mb-16 flex flex-col gap-8">
        {run && (
          <div className="text-sm text-text-secondary">
            Started by <span className="font-bold">{run.startedByEmail}</span> at {formatRunDate(run.startedAt)}
            {run.finishedAt && <> · Finished at {formatRunDate(run.finishedAt)}</>}
          </div>
        )}
        {alreadyFlagged && (
          <div className="text-sm text-text-secondary">
            🚩 Flagged for engineer review{run?.flagNote ? `: ${run.flagNote}` : ''}
          </div>
        )}
        {status === 'error' && runError && !folded.preconditionFailed && (
          <div className="rounded-6 border border-notification-border-error bg-notification-canvas-error p-16">
            <div className="font-bold">Run failed</div>
            <div className="text-sm text-text-error">{runError}</div>
          </div>
        )}
        {accountId && (
          <div className="flex items-center justify-between rounded-6 border border-notification-border-success bg-notification-canvas-success p-16">
            <div>
              <div className="font-bold">Account created</div>
              <div className="text-sm text-text-secondary">{accountId}</div>
            </div>
            <a href={`/accounts/${accountId}`} target="_blank" rel="noreferrer">
              <PalmettoButton type="button" emphasis="primary" size="small">
                Open account
              </PalmettoButton>
            </a>
          </div>
        )}
        {folded.hasFallbackWarning && (
          <div className="rounded-6 border border-notification-border-warning bg-notification-canvas-warning p-16">
            <div className="font-bold">Using your token (fallback)</div>
            <div className="text-sm">
              Test-user credentials failed to authenticate; the runner is using your own token. Update the test users
              at{' '}
              <Link to="/internal/accountctl/test-users" className="text-text-link">
                /internal/accountctl/test-users
              </Link>
              .
            </div>
          </div>
        )}
        {status === 'error' && folded.preconditionFailed && run?.scenarioId && (
          <PreconditionFixBanner
            scenarioId={run.scenarioId}
            fixable={folded.preconditionFailed.fixable}
            manual={folded.preconditionFailed.manual}
          />
        )}
      </div>

      {folded.preStepLogs.length > 0 && (
        <div className="mx-6 mb-16 flex flex-col gap-2 text-sm text-text-secondary">
          {folded.preStepLogs.map((log, i) => (
            <LogLine key={`pre-${i}`} level={log.level} message={log.message} />
          ))}
        </div>
      )}

      {/*
        The timeline is the page's whole point and it updates from the SSE
        stream rather than from user action, so it needs to announce itself.
        `polite` rather than `assertive`: a chatty run shouldn't interrupt
        whatever the user is reading.
      */}
      <div className="mx-6 flex flex-col gap-8" role="status" aria-live="polite" aria-relevant="additions text">
        {folded.steps.length === 0 ? (
          <div className="text-sm text-text-secondary">Waiting for the runner to start…</div>
        ) : (
          folded.steps.map((step) => <TimelineRow key={step.index} step={step} />)
        )}
      </div>
    </DsmPageContainer>
  );
};
